import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQnASessions, useQnAMessages, useQnAStream } from '@/Recoil/Chat';
import ChattingInput from '@/Components/Chat/ChattingInput';
import WaitingBubble from '@/Components/Chat/WaitingBubble';
import BubbleRender from '@/Components/Chat/BubbleRender';
import StatusNarration from '@/Components/Chat/StatusNarration';
import { styleSplitter, processMessageToBubbles, dateFormatter } from '@/Utils/message';
import type { BubbleRenderData } from '@/Utils/message';
import { handleLinkClick, navigateTo } from '@/Utils/routing';
import AddIcon from '@mui/icons-material/Add';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { usePurchaseHistory } from '@/Recoil/Payment/Purchase';
import './index.scss';
import { useSession } from '@/Recoil/Session/Session';
import DialogAlert from '@/Components/DialogAlert';

const PRODUCT_ID = 'mate_study_abroad.250917.01';

const QNA_BOT_NAME = 'Admission QnA';

// Webinar schedule (KST = UTC+9)
const WEBINAR_START = new Date().getTime(); //always before webinar
const WEBINAR_END   = new Date('2026-02-09T20:40:00+09:00').getTime();

interface AdmissionQnAProps {
    sessionid?: string;
}

export default function AdmissionQnA({ sessionid: initialSessionId }: AdmissionQnAProps) {
    const [inputValue, setInputValue] = useState('');
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const { waitSession } = useSession();
    const updateInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setInputValue(e.target.value), []);
    const { hasProduct, loading: purchaseLoading, error: purchaseError } = usePurchaseHistory();
    const hasPurchased = !purchaseError && !purchaseLoading && hasProduct(PRODUCT_ID);

    useEffect(() => {
        waitSession().then((session) => {
            if (!session.email) {
                DialogAlert.alert('Login Required','Please log in to access the Admission QnA feature. (Google SSO supported)').then(() => {
                    navigateTo(`/login?redirectTo=${encodeURIComponent(window.location.pathname)}`);
                })
                    
            }
        })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    // Webinar live check
    const [isLive, setIsLive] = useState(() => {
        const now = Date.now();
        return now >= WEBINAR_START && now <= WEBINAR_END;
    });

    useEffect(() => {
        const check = () => {
            const now = Date.now();
            setIsLive(now >= WEBINAR_START && now <= WEBINAR_END);
        };
        const interval = setInterval(check, 10_000);
        return () => clearInterval(interval);
    }, []);

    const { sessions, syncSessions, deleteSession, addSessionToState } = useQnASessions();

    // Track active session locally — initialize from URL param
    const [selectedSessionId, setSelectedSessionId] = useState<string | null>(initialSessionId ?? null);

    const { messages, syncMessages } = useQnAMessages(selectedSessionId ?? '');
    const { isStreaming, streamingContent, statusContent, sendMessage } = useQnAStream(selectedSessionId);

    // Convert messages to bubbles
    const bubbles = useMemo(() => {
        const sorted = [...messages].sort((a, b) => a.created_at - b.created_at);
        const result: BubbleRenderData[] = [];

        sorted.forEach((message, index) => {
            const prev = index > 0 ? sorted[index - 1] : undefined;
            const isUser = message.role === 'user';
            const adapted = {
                messageid: message.messageid,
                content:   message.content,
                addressid: isUser ? 'user' : 'mr_mate',
                images:    [] as string[],
                created_at: message.created_at,
            };
            const adaptedPrev = prev ? {
                messageid: prev.messageid,
                content:   prev.content,
                addressid: prev.role === 'user' ? 'user' : 'mr_mate',
                images:    [] as string[],
                created_at: prev.created_at,
            } : undefined;

            const messageBubbles = processMessageToBubbles(
                adapted,
                adaptedPrev,
                isUser ? undefined : { name: QNA_BOT_NAME },
                isUser ? 'user' : 'assistant'
            );
            result.push(...messageBubbles);
        });

        return result;
    }, [messages]);

    // Sync sessions on mount
    useEffect(() => {
        syncSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Sync messages when session changes
    useEffect(() => {
        if (selectedSessionId) {
            syncMessages();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSessionId]);

    // Auto-scroll on new messages
    const messagesEndRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [bubbles.length, streamingContent, statusContent]);

    // Submit handler — supports auto-session creation
    const submitThrottle = useRef<boolean>(false);
    const handleSubmit = useCallback((e: React.FormEvent) => {
        e.preventDefault();
        if (!inputValue.trim() || isStreaming) return;
        if (submitThrottle.current) return;

        submitThrottle.current = true;
        const message = inputValue.trim();
        setInputValue('');

        sendMessage(message, {
            onSessionCreated: (newSessionId) => {
                // Stream auto-created a session, add to state and navigate
                addSessionToState({
                    sessionid: newSessionId,
                    userid: '',
                    title: message.slice(0, 50),
                    status: 'active',
                    created_at: Math.floor(Date.now() / 1000),
                    updated_at: Math.floor(Date.now() / 1000),
                });
                setSelectedSessionId(newSessionId);
                navigateTo(`/admission/qna/${newSessionId}`);
            },
        }).then(() => {
            (e.target as HTMLFormElement).querySelector('input')?.focus();
            // Refresh sessions to get updated titles
            syncSessions();
        });

        submitThrottle.current = false;
    }, [inputValue, isStreaming, sendMessage, addSessionToState, syncSessions]);

    // New chat handler — just deselect, stream will auto-create on first message
    const handleNewChat = useCallback(() => {
        setSelectedSessionId(null);
        navigateTo('/admission/qna');
        setSidebarOpen(false);
    }, []);

    // Select session handler
    const handleSelectSession = useCallback((sessionid: string) => {
        setSelectedSessionId(sessionid);
        navigateTo(`/admission/qna/${sessionid}`);
        setSidebarOpen(false);
    }, []);

    // Delete session handler
    const handleDeleteSession = useCallback(async (e: React.MouseEvent, sessionid: string) => {
        e.stopPropagation();
        const success = await deleteSession(sessionid);
        if (success && selectedSessionId === sessionid) {
            const next = sessions.find(s => s.sessionid !== sessionid)?.sessionid ?? null;
            setSelectedSessionId(next);
            navigateTo(next ? `/admission/qna/${next}` : '/admission/qna');
        }
    }, [deleteSession, selectedSessionId, sessions]);

    return (
        <div className="QnAPage">
            {/* Mobile overlay */}
            {sidebarOpen && <div className="SidebarOverlay" onClick={() => setSidebarOpen(false)} />}

            {/* Sidebar */}
            <aside className={`Sidebar ${sidebarOpen ? 'Open' : ''}`}>
                <div className="SidebarHeader">
                    <h2>QnA Sessions</h2>
                    <button className="CloseButton" onClick={() => setSidebarOpen(false)}>
                        <CloseIcon />
                    </button>
                </div>
                <button className="NewChatButton" onClick={handleNewChat}>
                    <AddIcon /> New Chat
                </button>
                <div className="SessionList">
                    {sessions.map(session => (
                        <div
                            key={session.sessionid}
                            className={`SessionItem ${session.sessionid === selectedSessionId ? 'Active' : ''}`}
                            onClick={() => handleSelectSession(session.sessionid)}
                        >
                            <div className="SessionInfo">
                                <span className="SessionTitle">
                                    {session.title || 'New conversation'}
                                </span>
                                <span className="SessionDate">
                                    {dateFormatter(session.updated_at)}
                                </span>
                            </div>
                            <button
                                className="DeleteButton"
                                onClick={(e) => handleDeleteSession(e, session.sessionid)}
                            >
                                <DeleteOutlineIcon fontSize="small" />
                            </button>
                        </div>
                    ))}
                    {sessions.length === 0 && (
                        <div className="EmptyState">
                            No conversations yet.<br />
                            Start a new chat!
                        </div>
                    )}
                </div>
            </aside>

            {/* Chat Area */}
            <div className="ChatArea">
                <div className="ChatHeader">
                    <button className="MenuButton" onClick={() => setSidebarOpen(true)}>
                        <MenuIcon />
                    </button>
                    <h1>{isLive ? 'GKS document review Webinar is On Air' : 'Admission QnA'}</h1>
                    {hasPurchased
                        ? <span className="PurchaseBadge Verified"><CheckCircleIcon fontSize="small" /> Verified</span>
                        : <a className="PurchaseBadge Register" href="/product/study-abroad-mate" onClick={handleLinkClick}>Register</a>
                    }
                    {isLive && <span className="LiveBadge"><span className="LiveDot" /> LIVE</span>}
                </div>

                <main className="ChatContent">
                    <div className="MessagesContainer">
                        <div className="Messages">
                            {!selectedSessionId && bubbles.length === 0 && !isStreaming && (
                                <div className="WelcomeMessage">
                                    <h2>Welcome to Admission QnA</h2>
                                    <p>Ask any questions about Korean university admissions, GKS scholarship, visa, and more.</p>
                                </div>
                            )}

                            {bubbles.map((bubble) => (
                                <BubbleRender
                                    key={bubble.id}
                                    item={bubble}
                                />
                            ))}

                            {isStreaming && statusContent && (
                                <StatusNarration>{statusContent}</StatusNarration>
                            )}

                            {isStreaming && !streamingContent && !statusContent && (
                                <WaitingBubble senderName={QNA_BOT_NAME} senderid="mr_mate" />
                            )}

                            {isStreaming && streamingContent && streamingContent.split('\n\n').map((part, i, arr) =>
                                <BubbleRender
                                    key={`streaming-${i}`}
                                    item={{
                                        id: `reading-${selectedSessionId || 'new'}`,
                                        messageid: `reading-${selectedSessionId || 'new'}`,
                                        type: 'text',
                                        text: part,
                                        segments: styleSplitter(part),
                                        role: 'assistant',
                                        senderid: 'mr_mate',
                                        senderName: QNA_BOT_NAME,
                                        showSenderInfo: i === 0,
                                        isLastBubble: i === arr.length - 1,
                                    }}
                                />
                            )}

                            <div ref={messagesEndRef} />
                        </div>
                    </div>
                </main>

                <ChattingInput
                    value={inputValue}
                    onChange={updateInput}
                    onSubmit={handleSubmit}
                    placeholder="Ask about admissions..."
                    disabled={isStreaming}
                />

            </div>
        </div>
    );
}
